import type { MetaFunction } from '@remix-run/node'

import { Code, Entry, ParamTable, Pill, Section } from '~/components/marketing/DocsKit'

export const meta: MetaFunction = () => [
  { title: 'MCP — Quiver Docs' },
  {
    name: 'description',
    content:
      'Connect Claude or any MCP client to a Quiver workspace. The hosted MCP server self-describes your config and exposes read/write tools over the same API.',
  },
]

const TOOLS: { name: string; args: string; what: string }[] = [
  { name: 'orient', args: '—', what: 'Cold-start bundle: purpose, singletons, active work, counts, recent activity.' },
  { name: 'workspace_info', args: '—', what: 'Full config: every type, field, status, metric, view.' },
  { name: 'get_type_config', args: 'type?', what: 'One type’s schema (or a catalog of all types).' },
  { name: 'list_files', args: 'type? status? q? limit?', what: 'Browse files; status accepts open/active/closed tokens.' },
  { name: 'search', args: 'query', what: 'Recall by content — titles and all data-field values.' },
  { name: 'get_file', args: 'id', what: 'One file’s title, status, and resolved field values.' },
  { name: 'get_file_data', args: 'id', what: 'Just the data fields (enums labeled, refs resolved).' },
  { name: 'related_files', args: 'id', what: 'Every file that references this one (reverse links).' },
  { name: 'file_activity', args: 'id', what: 'Change history for one file.' },
  { name: 'recent_activity', args: 'limit?', what: 'Workspace-wide recent changes.' },
  { name: 'get_view', args: 'viewId period?', what: 'A dashboard view’s curated files + metric cards.' },
  { name: 'list_metrics', args: '—', what: 'Available metrics.' },
  { name: 'get_metric', args: 'id period?', what: 'A single metric’s value + target.' },
  { name: 'create_file', args: 'type title? data? parent? force?', what: 'Create a file and populate fields in one call.' },
  { name: 'update_file', args: 'id fields? title?', what: 'Set fields and/or rename atomically.' },
  { name: 'set_status', args: 'id status', what: 'Move a file to a status.' },
  { name: 'tell', args: 'message', what: 'Hand a multi-part update to the workspace agent (last resort).' },
  { name: 'run_file_agent', args: 'id message', what: 'File-scoped agent escape hatch.' },
  { name: 'usage_stats / storage_stats', args: '…', what: 'Token spend and storage footprint.' },
]

export default function DocsMcp() {
  return (
    <div className="space-y-14">
      <section>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-300">Reference</p>
        <h1 className="mt-2 text-4xl font-bold tracking-tight text-white sm:text-5xl">MCP</h1>
        <p className="mt-5 max-w-2xl text-lg text-zinc-400">
          A hosted Model Context Protocol server at{' '}
          <span className="font-mono text-zinc-300">https://mcp.quiveros.ai</span>. Point Claude (or any MCP client) at
          it with a workspace key, and it exposes your dashboard as tools — self-described from your config, no glue code.
        </p>
      </section>

      <Section eyebrow="Connect" title="Add the server">
        <Entry header={<span className="text-base font-semibold text-zinc-100">Endpoint & auth</span>} pills={<Pill tone="indigo">Bearer qvr_</Pill>}>
          <p className="text-sm text-zinc-400">
            Streamable-HTTP at <span className="font-mono text-zinc-300">/mcp</span>. Authenticate with a{' '}
            <span className="font-mono text-zinc-300">qvr_</span> workspace key as a bearer token — the connection is
            bound to that one workspace. (Clients without a key fall into an OAuth flow.)
          </p>
          <Code label="Claude Desktop · claude_desktop_config.json">{`{
  "mcpServers": {
    "quiver": {
      "url": "https://mcp.quiveros.ai/mcp",
      "headers": { "Authorization": "Bearer qvr_xxxxxxxxxxxx" }
    }
  }
}`}</Code>
          <p className="text-sm text-zinc-500">
            Connecting more than one workspace in Claude Desktop? Add another entry with that workspace’s key — each is
            scoped by its bearer. (Web connectors that key off the URL alone may need a distinct URL per workspace —
            per-workspace endpoints are on the roadmap.)
          </p>
        </Entry>
        <Entry header={<span className="text-base font-semibold text-zinc-100">It describes itself</span>}>
          <p className="text-sm text-zinc-400">
            On connect, the server reads your workspace config and tells the assistant exactly what exists — your file
            types, their fields and statuses, and your metrics — and exposes a live{' '}
            <span className="font-mono text-zinc-300">quiver://workspace</span> resource that re-fetches per call, so
            schema edits show up without reconnecting. There’s no domain logic in the server; every tool is shaped by
            your config.
          </p>
        </Entry>
      </Section>

      <Section
        eyebrow="Tools"
        title="What the assistant can do"
        intro="All tools hit the same /api routes as the REST API and CLI — same backend, same behavior. Reads are cheap; the agent escape hatches (tell, run_file_agent) are a last resort that spend tokens."
      >
        <Entry header={<span className="text-base font-semibold text-zinc-100">Tool catalog</span>}>
          <ParamTable head="Tool" rows={TOOLS.map((t) => [t.name, t.args, t.what])} />
        </Entry>
      </Section>

      <Section eyebrow="In practice" title="What a session looks like">
        <Code label="conversation">{`You:  How's the leaderboard doing?
  → orient            (purpose, counts, recent plays)
  → get_metric        id: leaderboard
Claude: 54 games logged; top score 3,360 (Teal Bowman). 2 playing right now.

You:  Log a 320 for Bob on hard.
  → create_file       type: score, data: { player: "Bob", value: 320, category: "hard" }
Claude: Done — Bob's on the board at #4 in hard.`}</Code>
        <p className="text-sm text-zinc-500">
          Same vocabulary as the <span className="font-mono text-zinc-300">quiver</span> CLI and the REST API — the MCP
          server is just the door for AI clients.
        </p>
      </Section>
    </div>
  )
}
